import { Alert, Spinner } from '@nrx/ui';
import { useRef, useState } from 'react';

export interface UploadDropzoneProps {
  readonly onFiles: (files: File[]) => void;
  readonly uploading: boolean;
  readonly progress: number;
  readonly error: string | null;
  /** Forwarded to the hidden file input's `accept` attribute. */
  readonly accept?: string;
  /** Forwarded to the hidden file input's `multiple` attribute. @defaultValue true */
  readonly multiple?: boolean;
}

export function UploadDropzone({
  onFiles,
  uploading,
  progress,
  error,
  accept,
  multiple = true,
}: UploadDropzoneProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div>
      <div
        data-testid="fx-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const files = Array.from(e.dataTransfer?.files ?? []);
          if (files.length) onFiles(files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center text-sm ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 text-gray-500'}`}
      >
        Drag files here, or click to choose
        <input
          ref={inputRef}
          data-testid="fx-file-input"
          type="file"
          multiple={multiple}
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) onFiles(files);
          }}
        />
      </div>
      {uploading && (
        <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <Spinner label="Uploading" /> Uploading… {progress}%
        </p>
      )}
      {error && (
        <Alert tone="danger" className="mt-2">
          {error}
        </Alert>
      )}
    </div>
  );
}
